import { useQuery } from '@apollo/client'
import { Link } from 'react-router-dom'
import { GET_STOREFRONT_HOME } from '../../graphql/operations'
import { formatPrice } from '../../lib/format'

interface ProductLite {
  id: string
  title: string
  currency: string
  imageUrls: string[] | null
  productVariants: { id: string; price: number }[]
}
interface CatalogNode extends ProductLite {
  stores: { id: string; name: string; logoUrl: string | null }[]
  mall: { id: string; name: string; logoUrl: string | null } | null
}
interface Stream {
  id: string
  title: string
  status: string
  startedAt: string | null
  viewerCount: number
  isLive: boolean
  isUpcoming: boolean
  isEnded: boolean
  store: { id: string; name: string }
  livestreamProducts: { product: { id: string; title: string; imageUrls: string[] | null } }[]
}

function minPrice(p: ProductLite): number | null {
  const prices = p.productVariants.map((v) => v.price).filter((n) => n > 0)
  return prices.length ? Math.min(...prices) : null
}

function whenLabel(s: Stream): string {
  if (s.isLive) return 'LIVE NOW'
  if (s.isEnded) return 'Replay'
  if (s.startedAt) {
    const d = new Date(s.startedAt)
    return d.toLocaleString(undefined, { weekday: 'short', hour: '2-digit', minute: '2-digit' })
  }
  return 'Upcoming'
}

function StreamCard({ s }: { s: Stream }) {
  const img = s.livestreamProducts[0]?.product.imageUrls?.[0]
  return (
    <div className={`stream-card ${s.isLive ? 'is-live' : ''}`} data-testid="stream-card">
      <div className="stream-thumb">
        {img ? <img src={img} alt="" loading="lazy" /> : <span className="stream-noimg">▶</span>}
        <span className={`stream-badge ${s.isLive ? 'live' : s.isEnded ? 'replay' : 'soon'}`}>{whenLabel(s)}</span>
        {s.isLive && <span className="stream-viewers">{s.viewerCount} watching</span>}
      </div>
      <div className="stream-meta">
        <div className="stream-title">{s.title}</div>
        <div className="stream-store">{s.store.name}</div>
      </div>
    </div>
  )
}

function ProductCard({ p }: { p: ProductLite }) {
  const min = minPrice(p)
  return (
    <Link to={`/products/${p.id}`} className="shop-card" data-testid="shop-card">
      <div className="shop-card-img">
        {p.imageUrls?.[0] ? <img src={p.imageUrls[0]} alt={p.title} loading="lazy" /> : <span className="shop-card-noimg">No image</span>}
      </div>
      <div className="shop-card-title">{p.title}</div>
      {min != null && <div className="shop-card-price">{formatPrice(min, p.currency)}</div>}
    </Link>
  )
}

function Row({ title, id, children, empty }: { title: string; id?: string; children: React.ReactNode; empty?: string }) {
  return (
    <section className="home-section" id={id}>
      <h2 className="home-section-title">{title}</h2>
      {/* callers pass `{cond && <JSX/>}`, which is `false` (not nullish) when
          empty — so use a truthy check, not ??, or the fallback never shows. */}
      {children || <p className="home-empty">{empty}</p>}
    </section>
  )
}

export default function Home() {
  const { data, loading, error } = useQuery(GET_STOREFRONT_HOME, { fetchPolicy: 'cache-and-network' })

  if (loading && !data) return <div className="shop-state" data-testid="home-loading">Loading the marketplace…</div>
  if (error) return <div className="error" data-testid="home-error">Couldn’t load the storefront: {error.message}</div>

  const featured: ProductLite[] = data?.featured ?? []
  const live: Stream[] = data?.live ?? []
  const upcoming: Stream[] = data?.upcoming ?? []
  const past: Stream[] = data?.past ?? []
  const catalog: CatalogNode[] = (data?.catalog?.edges ?? []).map((e: { node: CatalogNode }) => e.node)

  // Derive brand + mall rails from the catalog (no public list query exists).
  const brands = new Map<string, { id: string; name: string; logoUrl: string | null; count: number }>()
  const malls = new Map<string, { id: string; name: string; logoUrl: string | null; count: number }>()
  for (const p of catalog) {
    for (const s of p.stores ?? []) {
      const e = brands.get(s.id) ?? { ...s, count: 0 }
      e.count++; brands.set(s.id, e)
    }
    if (p.mall) {
      const e = malls.get(p.mall.id) ?? { ...p.mall, count: 0 }
      e.count++; malls.set(p.mall.id, e)
    }
  }
  const liveAndUpcoming = [...live, ...upcoming]

  return (
    <div className="home" data-testid="home">
      {/* Hero */}
      <section className="home-hero">
        <div className="home-hero-text">
          <h1>Shop live. Talk to a stylist.</h1>
          <p>Browse the malls, catch a livestream, or ask our AI stylist about any product — as a guest or signed in.</p>
          <a href="#offers" className="btn-primary">Shop special offers</a>
        </div>
      </section>

      {/* Live & upcoming */}
      <Row title="Live & upcoming" id="live"
           empty="No streams scheduled right now — check back soon.">
        {liveAndUpcoming.length > 0 && (
          <div className="stream-row">
            {liveAndUpcoming.map((s) => <StreamCard key={s.id} s={s} />)}
          </div>
        )}
      </Row>

      {/* Special offers (featured) */}
      <Row title="Special offers" id="offers" empty="No featured products yet.">
        {featured.length > 0 && (
          <div className="shop-grid">{featured.map((p) => <ProductCard key={p.id} p={p} />)}</div>
        )}
      </Row>

      {/* Brands */}
      <Row title="Shop by brand" id="brands" empty="No brands to show yet.">
        {brands.size > 0 && (
          <div className="tile-row">
            {[...brands.values()].map((b) => (
              <div key={b.id} className="brand-tile" data-testid="brand-tile">
                <div className="brand-logo">{b.logoUrl ? <img src={b.logoUrl} alt={b.name} /> : b.name.charAt(0)}</div>
                <div className="brand-name">{b.name}</div>
                <div className="brand-count">{b.count} products</div>
              </div>
            ))}
          </div>
        )}
      </Row>

      {/* Malls */}
      <Row title="Malls" id="malls" empty="No malls to show yet.">
        {malls.size > 0 && (
          <div className="tile-row">
            {[...malls.values()].map((m) => (
              <div key={m.id} className="mall-tile" data-testid="mall-tile">
                <div className="mall-logo">{m.logoUrl ? <img src={m.logoUrl} alt={m.name} /> : '🏬'}</div>
                <div className="brand-name">{m.name}</div>
                <div className="brand-count">{m.count} products</div>
              </div>
            ))}
          </div>
        )}
      </Row>

      {/* Watch again */}
      <Row title="Watch again" empty="No past streams yet.">
        {past.length > 0 && (
          <div className="stream-row">{past.map((s) => <StreamCard key={s.id} s={s} />)}</div>
        )}
      </Row>

      {/* Full catalog */}
      <Row title="The collection">
        <div className="shop-grid">{catalog.map((p) => <ProductCard key={p.id} p={p} />)}</div>
      </Row>
    </div>
  )
}
